import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { truckService } from '../api/api';
import ConfirmDialog from './ConfirmDialog';
import { formatPortalDate } from '../utils/dateFormat';

const GLOVE_BOX_TITLE_OPTIONS = [
  'National Safety Fitness',
  'SGI Inspection Record',
  'SGI Inspection Certificate',
  'SGI Registration',
  'AutoPak',
  'General Liability Insurance',
];

const DEFAULT_GLOVE_BOX_EMAIL_RECIPIENTS = 'agm@biomedwaste.com, partsandservice@biomedwaste.com';

const emptyForm = {
  title: '',
  expires_at: '',
  reminder_date: '',
  email_enabled: true,
  email_recipients: DEFAULT_GLOVE_BOX_EMAIL_RECIPIENTS,
  notes: '',
  document: null,
};

function formatDate(value) {
  if (!value) return 'No expiry';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return formatPortalDate(date);
}

function splitEmailValues(value) {
  return String(value || '')
    .split(/[,\n;]/)
    .map((email) => email.trim())
    .filter(Boolean);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function normalizeEmailValues(value) {
  return Array.from(new Set(splitEmailValues(value).filter(isValidEmail))).join(', ');
}

function invalidEmailValues(value) {
  return splitEmailValues(value).filter((email) => !isValidEmail(email));
}

function isPdf(doc) {
  return String(doc?.mime_type || doc?.url || '').toLowerCase().includes('pdf');
}

function isImage(doc) {
  return String(doc?.mime_type || '').toLowerCase().startsWith('image/');
}

function DocumentThumb({ doc, onClick }) {
  if (isImage(doc)) {
    return (
      <Box
        component="img"
        src={doc.url}
        alt={doc.title}
        onClick={onClick}
        sx={{
          width: 112,
          height: 86,
          objectFit: 'contain',
          bgcolor: '#f8fafc',
          border: '1px solid #d8e2ef',
          borderRadius: 1.5,
          cursor: 'zoom-in',
        }}
      />
    );
  }

  return (
    <Box
      onClick={onClick}
      sx={{
        width: 112,
        height: 86,
        bgcolor: '#fff5f5',
        border: '1px solid #ffd6d6',
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      {isPdf(doc) ? <PictureAsPdfIcon sx={{ fontSize: 44, color: '#c62828' }} /> : <DescriptionIcon sx={{ fontSize: 44, color: '#001f3f' }} />}
    </Box>
  );
}

export default function TruckGloveBox({ truckId }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadDocuments = async () => {
    if (!truckId) return;
    try {
      setLoading(true);
      const res = await truckService.getGloveBoxDocuments(truckId);
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to load glove box documents.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [truckId]);

  const filteredDocuments = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = !q
      ? documents
      : documents.filter((doc) => (
        doc.title?.toLowerCase().includes(q) ||
        doc.notes?.toLowerCase().includes(q) ||
        doc.original_name?.toLowerCase().includes(q) ||
        formatDate(doc.expires_at).toLowerCase().includes(q)
      ));
    return rows.slice(0, rowsPerPage);
  }, [documents, rowsPerPage, search]);

  const openAdd = () => {
    setEditingDoc(null);
    setForm(emptyForm);
    setEmailInput('');
    setDialogOpen(true);
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setForm({
      title: doc.title || '',
      expires_at: doc.expires_at || '',
      reminder_date: doc.reminder_date || '',
      email_enabled: doc.email_enabled === true,
      email_recipients: doc.email_recipients || '',
      notes: doc.notes || '',
      document: null,
    });
    setEmailInput('');
    setDialogOpen(true);
  };

  const commitEmailInput = () => {
    const nextInput = emailInput.trim();
    if (!nextInput) return;
    setForm((prev) => ({
      ...prev,
      email_recipients: normalizeEmailValues(`${prev.email_recipients || ''},${nextInput}`),
    }));
    setEmailInput('');
  };

  const handleSave = async () => {
    if (!truckId) return;
    if (!editingDoc && !form.document) {
      setSnackbar({ open: true, severity: 'error', message: 'Please choose a PDF, JPG, or PNG document.' });
      return;
    }
    if (form.expires_at && form.reminder_date && form.reminder_date > form.expires_at) {
      setSnackbar({ open: true, severity: 'error', message: 'Reminder date must be on or before the expiry date.' });
      return;
    }
    const emailRecipients = normalizeEmailValues(`${form.email_recipients || ''},${emailInput || ''}`);
    const invalidEmails = invalidEmailValues(`${form.email_recipients || ''},${emailInput || ''}`);
    if (form.email_enabled && !emailRecipients) {
      setSnackbar({ open: true, severity: 'error', message: 'Add at least one email recipient.' });
      return;
    }
    if (invalidEmails.length) {
      setSnackbar({ open: true, severity: 'error', message: `Invalid email: ${invalidEmails[0]}` });
      return;
    }

    const payload = new FormData();
    payload.append('title', form.title.trim());
    payload.append('expires_at', form.expires_at || '');
    payload.append('reminder_date', form.reminder_date || '');
    payload.append('email_enabled', form.email_enabled ? 'true' : 'false');
    payload.append('email_recipients', form.email_enabled ? emailRecipients : '');
    payload.append('notes', form.notes || '');
    if (form.document) payload.append('document', form.document);

    try {
      setSaving(true);
      if (editingDoc) {
        await truckService.updateGloveBoxDocument(truckId, editingDoc.id, payload);
      } else {
        await truckService.createGloveBoxDocument(truckId, payload);
      }
      setDialogOpen(false);
      setEditingDoc(null);
      setForm(emptyForm);
      await loadDocuments();
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('remindersUpdated'));
      setSnackbar({ open: true, severity: 'success', message: editingDoc ? 'Document updated.' : 'Document uploaded.' });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to save document.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!truckId || !deleteDoc) return;
    try {
      await truckService.deleteGloveBoxDocument(truckId, deleteDoc.id);
      setDeleteDoc(null);
      await loadDocuments();
      window.dispatchEvent(new Event('dataUpdated'));
      window.dispatchEvent(new Event('remindersUpdated'));
      setSnackbar({ open: true, severity: 'success', message: 'Document deleted.' });
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: error?.response?.data?.error || 'Failed to delete document.',
      });
    }
  };

  const handlePrint = (doc) => {
    if (!doc?.url) return;
    const printWindow = window.open(doc.url, '_blank');
    if (printWindow) {
      printWindow.onload = () => printWindow.print();
    }
  };

  const actionButtons = (doc) => (
    <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'center', flexWrap: 'wrap' }}>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => openEdit(doc)} sx={{ border: '1px solid #d8e2ef', borderRadius: 1 }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="View">
        <IconButton size="small" onClick={() => setPreviewDoc(doc)} sx={{ border: '1px solid #d8e2ef', borderRadius: 1 }}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" color="error" onClick={() => setDeleteDoc(doc)} sx={{ border: '1px solid #ffd6d6', borderRadius: 1 }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Print">
        <IconButton size="small" onClick={() => handlePrint(doc)} sx={{ border: '1px solid #d8e2ef', borderRadius: 1 }}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <Box>
      <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: { xs: 1, sm: 1.25 }, mb: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{
                width: { xs: 40, sm: 46 },
                height: { xs: 40, sm: 46 },
                borderRadius: 2,
                bgcolor: '#001f3f',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <FolderIcon sx={{ fontSize: { xs: 23, sm: 27 } }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 900, color: '#001f3f', fontSize: { xs: '1rem', sm: '1.18rem', md: '1.3rem' }, lineHeight: 1.05, mb: 0.1 }}>
                  Glove Box
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.76rem' }, lineHeight: 1.25 }}>
                  {documents.length} document{documents.length === 1 ? '' : 's'} stored in the database
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{
                bgcolor: '#c62828',
                fontWeight: 900,
                borderRadius: 1.6,
                px: 1.55,
                py: 0.62,
                minHeight: 36,
                fontSize: { xs: '0.78rem', sm: '0.86rem' },
                '&:hover': { bgcolor: '#a61f1f' },
              }}
            >
              Add Item
            </Button>
          </Box>

          <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e5eaf2', boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ width: 78 }}>
                    <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={150}>150</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2">records per page</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : filteredDocuments.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 3 }}>
              <FolderIcon sx={{ fontSize: 64, color: '#b0bec5', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No glove box documents found
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredDocuments.map((doc) => (
                <Paper key={doc.id} sx={{ p: 2, borderRadius: 3, border: '1px solid #d8e2ef' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <DocumentThumb doc={doc} onClick={() => setPreviewDoc(doc)} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{doc.title}</Typography>
                      <Chip size="small" label={formatDate(doc.expires_at)} sx={{ my: 1, fontWeight: 700 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {doc.notes || 'No notes'}
                      </Typography>
                      {doc.reminder_date && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mt: 1 }}>
                          <Chip
                            size="small"
                            icon={<NotificationsActiveIcon />}
                            label={`Reminder: ${formatDate(doc.reminder_date)}`}
                            color="warning"
                            sx={{ fontWeight: 800 }}
                          />
                          {doc.email_enabled && <Chip size="small" label="Email On" color="info" sx={{ fontWeight: 800 }} />}
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ mt: 2 }}>{actionButtons(doc)}</Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer sx={{ border: '1px solid #d8e2ef', borderRadius: 2, overflowX: 'auto' }}>
              <Table stickyHeader sx={{ minWidth: 950 }}>
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: '#001f3f', color: 'white', fontWeight: 900 } }}>
                    <TableCell sx={{ width: 130 }}>Document</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell sx={{ width: 180 }}>Expires</TableCell>
                    <TableCell sx={{ width: 190 }}>Reminder</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="center" sx={{ width: 230 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocuments.map((doc) => (
                    <TableRow key={doc.id} hover sx={{ verticalAlign: 'top' }}>
                      <TableCell>
                        <DocumentThumb doc={doc} onClick={() => setPreviewDoc(doc)} />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 900, color: '#001f3f' }}>{doc.title}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {doc.original_name || 'Uploaded document'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={formatDate(doc.expires_at)} size="small" sx={{ fontWeight: 800 }} />
                      </TableCell>
                      <TableCell>
                        {doc.reminder_date ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7, alignItems: 'flex-start' }}>
                            <Chip icon={<NotificationsActiveIcon />} label={formatDate(doc.reminder_date)} color="warning" size="small" sx={{ fontWeight: 800 }} />
                            {doc.email_enabled && <Chip size="small" label="Email On" color="info" sx={{ fontWeight: 800 }} />}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Not set</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', minWidth: 260 }}>
                        {doc.notes || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {actionButtons(doc)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900, color: '#001f3f' }}>
          {editingDoc ? 'Edit Glove Box Document' : 'Add Glove Box Document'}
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 12, top: 10 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                freeSolo
                options={GLOVE_BOX_TITLE_OPTIONS}
                value={form.title}
                onChange={(_event, value) => setForm((prev) => ({ ...prev, title: value || '' }))}
                onInputChange={(_event, value) => setForm((prev) => ({ ...prev, title: value || '' }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Title"
                    placeholder="Choose a common title or type your own"
                    helperText="Optional. Choose a common title or type your own."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm((prev) => ({ ...prev, expires_at: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Optional."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Set Expiry Reminder"
                type="date"
                value={form.reminder_date}
                onChange={(e) => setForm((prev) => ({ ...prev, reminder_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Optional. Pick the date the reminder should appear."
              />
            </Grid>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f8fafc' }}>
                <FormControlLabel
                  control={<Switch checked={form.email_enabled} onChange={(e) => setForm((prev) => ({ ...prev, email_enabled: e.target.checked }))} />}
                  label={<Typography sx={{ fontWeight: 900, color: '#001f3f' }}>Send Email For This Expiry Reminder</Typography>}
                />
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={splitEmailValues(form.email_recipients)}
                  inputValue={emailInput}
                  onInputChange={(_event, value) => setEmailInput(value)}
                  onChange={(_event, value) => setForm((prev) => ({ ...prev, email_recipients: normalizeEmailValues(value.join(',')) }))}
                  disabled={!form.email_enabled}
                  renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      color={isValidEmail(option) ? 'info' : 'error'}
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Email Recipients"
                      placeholder="Type email and press Enter"
                      helperText="Add multiple recipients as chips. Commas, semicolons, and new lines are supported."
                      onBlur={commitEmailInput}
                    />
                  )}
                  sx={{ mt: 1 }}
                />
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Notes"
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{
                  py: 1.5,
                  borderStyle: 'dashed',
                  borderWidth: 2,
                  fontWeight: 900,
                  borderRadius: 2,
                }}
              >
                {form.document ? form.document.name : editingDoc ? 'Replace document (optional)' : 'Upload PDF, JPG, or PNG'}
                <input
                  hidden
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                  onChange={(e) => setForm((prev) => ({ ...prev, document: e.target.files?.[0] || null }))}
                />
              </Button>
              {editingDoc?.original_name && !form.document && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Current file: {editingDoc.original_name}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: '#001f3f', fontWeight: 900 }}>
            {saving ? 'Saving…' : 'Save Document'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(previewDoc)} onClose={() => setPreviewDoc(null)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#001f3f', color: 'white' }}>
          <Typography sx={{ fontWeight: 900 }}>{previewDoc?.title || 'Document Preview'}</Typography>
          <IconButton onClick={() => setPreviewDoc(null)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#0b1b2a' }}>
          {previewDoc ? (
            isImage(previewDoc) ? (
              <Box sx={{ height: { xs: '75vh', md: '80vh' }, p: 2, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box component="img" src={previewDoc.url} alt={previewDoc.title} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
            ) : (
              <Box component="iframe" src={previewDoc.url} title={previewDoc.title} sx={{ width: '100%', height: { xs: '75vh', md: '80vh' }, border: 0, bgcolor: 'white' }} />
            )
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteDoc)}
        title="Delete Document?"
        description={`This will permanently remove "${deleteDoc?.title || 'this document'}" from the database.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleDelete}
        onClose={() => setDeleteDoc(null)}
        icon={<DeleteIcon />}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
